import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  MapPin,
  Wallet,
  Clock,
  ShieldCheck,
  PawPrint,
  Laptop2,
  Coffee,
  Dumbbell,
  Home,
  CheckCircle2,
  Heart,
  Sun,
  Moon,
} from 'lucide-react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '@config/firebase';
import { useAuth } from '@context/AuthContext';
import { useUser } from '@hooks/useUsers';
import BottomNav from '@components/BottomNav';
import { getProvinces, getMetrosForProvince, getNeighborhoodsForMetro, type Province } from '@utils/locations';

type StepId = 'basics' | 'lifestyle' | 'home' | 'review';

interface Step {
  id: StepId;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface TenantFormData {
  fullName: string;
  occupation: string;
  location: string; // Legacy field for backward compatibility
  province: string;
  provinceCode: string;
  metro: string;
  moveInDate: string;
  budgetMin: number | null;
  budgetMax: number | null;
  vibe: string;
  dailyRhythms: string[];
  sleepSchedule: 'early' | 'flexible' | 'night' | null;
  cleanliness: 'tidy' | 'balanced' | 'relaxed' | null;
  lifestyleTags: string[];
  interests: string[];
  amenities: string[];
  neighborhoods: string[];
  householdStyles: string[];
  hasPets: boolean;
  worksRemote: boolean;
  bio: string;
}

const steps: Step[] = [
  {
    id: 'basics',
    title: 'Your basics',
    description: 'Set your location and timing',
    icon: <Sparkles className="w-4 h-4" />,
  },
  {
    id: 'lifestyle',
    title: 'Lifestyle & vibe',
    description: 'Habits, routines, and boundaries',
    icon: <Coffee className="w-4 h-4" />,
  },
  {
    id: 'home',
    title: 'Ideal home',
    description: 'Budget, amenities, and must-haves',
    icon: <Home className="w-4 h-4" />,
  },
  {
    id: 'review',
    title: 'Review & finish',
    description: 'Lock in details for better matches',
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
];

const lifestyleOptions = [
  'Non-smoker',
  'Into fitness',
  'Loves to cook',
  'Prefers quiet weekday nights',
  'Weekend adventures',
  'Minimalist',
  'Plants everywhere',
  'Open to hosting occasionally',
];

const amenityOptions = [
  'Private bathroom',
  'Desk space',
  'Secure parking',
  'In-unit laundry',
  'Fast Wi-Fi',
  'Balcony or garden',
  'Natural light',
  'Close to transit',
];

const interestOptions = [
  'Yoga / Gym',
  'Live music',
  'Ocean walks',
  'Photography',
  'Sunday markets',
  'Board games',
  'Cooking together',
  'Reading',
];

const vibeOptions = [
  'Calm & considerate',
  'Social but chill',
  'Neat and balanced',
  'Up for shared dinners',
  'Respectful of space',
];

const neighborhoodOptions = [
  'Sea Point',
  'Gardens',
  'Woodstock',
  'Observatory',
  'Claremont',
  'Rondebosch',
  'Green Point',
  'City Bowl',
];

const householdStyleOptions = [
  '2 bed apartment',
  'House share',
  'Garden access',
  'Secure building',
  'Pet-friendly',
  'Co-living space',
];

const TenantOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, refreshProfile } = useAuth();
  const { user, loading } = useUser(currentUser?.uid);
  const [currentStep, setCurrentStep] = useState<StepId>('basics');
  const [submitted, setSubmitted] = useState(false);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [availableMetros, setAvailableMetros] = useState<string[]>([]);
  const [availableNeighborhoods, setAvailableNeighborhoods] = useState<string[]>([]);
  const [formData, setFormData] = useState<TenantFormData>({
    fullName: '',
    occupation: '',
    location: '', // Legacy
    province: '',
    provinceCode: '',
    metro: '',
    moveInDate: '',
    budgetMin: null,
    budgetMax: null,
    vibe: '',
    dailyRhythms: [],
    sleepSchedule: null,
    cleanliness: null,
    lifestyleTags: [],
    interests: [],
    amenities: [],
    neighborhoods: [],
    householdStyles: [],
    hasPets: false,
    worksRemote: false,
    bio: '',
  });

  const stepIndex = useMemo(() => steps.findIndex((step) => step.id === currentStep), [currentStep]);
  const completion = useMemo(() => Math.round((stepIndex / (steps.length - 1)) * 100), [stepIndex]);

  // Load provinces on mount
  useEffect(() => {
    async function loadProvinces() {
      const data = await getProvinces();
      setProvinces(data);
    }
    loadProvinces();
  }, []);

  // Load existing user data when component mounts
  useEffect(() => {
    if (user && !loading) {
      // Backward compat: older profiles stored a single rhythm in `vibe`.
      let dailyRhythms: string[] = [];
      if (user.dailyRhythms && user.dailyRhythms.length > 0) {
        dailyRhythms = user.dailyRhythms;
      } else if (user.vibe && vibeOptions.includes(user.vibe)) {
        dailyRhythms = [user.vibe];
      }

      setFormData((prev) => ({
        ...prev,
        fullName: user.name || '',
        occupation: user.occupation || '',
        location: user.city || '',
        province: user.province || '',
        provinceCode: user.provinceCode || '',
        metro: user.metro || '',
        moveInDate: user.moveInDate || '',
        budgetMin: user.budgetMin || null,
        budgetMax: user.budgetMax || null,
        vibe: user.vibe || '',
        dailyRhythms,
        sleepSchedule: user.sleepSchedule || null,
        cleanliness: user.cleanliness || null,
        lifestyleTags: user.lifestyle || [],
        interests: user.interests || [],
        amenities: user.amenities || [],
        neighborhoods: user.neighborhoods || [],
        householdStyles: user.householdStyles || [],
        hasPets: user.petsPreference === 'yes',
        worksRemote: user.worksRemote || false,
        bio: user.bio || '',
      }));

      // Load metros and neighborhoods if province/metro are set
      if (user.provinceCode) {
        getMetrosForProvince(user.provinceCode).then(setAvailableMetros);
      }
      if (user.metro) {
        setAvailableNeighborhoods(getNeighborhoodsForMetro(user.metro));
      }
    }
  }, [user, loading]);

  const handleInputChange = <K extends keyof TenantFormData>(key: K, value: TenantFormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleProvinceChange = async (provinceCode: string) => {
    const selectedProvince = provinces.find((p) => p.code === provinceCode);
    if (selectedProvince) {
      setFormData((prev) => ({
        ...prev,
        provinceCode,
        province: selectedProvince.name,
        metro: '', // Reset metro when province changes
        neighborhoods: [], // Reset neighborhoods
      }));

      const metros = await getMetrosForProvince(provinceCode);
      setAvailableMetros(metros);
      setAvailableNeighborhoods([]);
    }
  };

  const handleMetroChange = (metro: string) => {
    setFormData((prev) => ({
      ...prev,
      metro,
      location: metro, // Update legacy field
      neighborhoods: [], // Reset neighborhoods when metro changes
    }));

    const neighborhoods = getNeighborhoodsForMetro(metro);
    setAvailableNeighborhoods(neighborhoods);
  };

  const toggleChip = (key: 'lifestyleTags' | 'interests' | 'amenities' | 'neighborhoods' | 'householdStyles' | 'dailyRhythms', value: string) => {
    setFormData((prev) => {
      const exists = prev[key].includes(value);
      const next = exists ? prev[key].filter((item) => item !== value) : [...prev[key], value];
      return { ...prev, [key]: next };
    });
  };

  const isStepComplete = (id: StepId) => {
    if (id === 'basics') {
      return (
        formData.fullName.trim().length > 1 &&
        formData.provinceCode.trim().length > 0 &&
        Boolean(formData.moveInDate)
      );
    }

    if (id === 'home') {
      return (
        formData.budgetMin !== null &&
        formData.budgetMax !== null &&
        formData.budgetMin > 0 &&
        formData.budgetMax > formData.budgetMin
      );
    }

    return true;
  };

  const onNext = async () => {
    // Save profile data on each step; only mark the profile complete on the
    // final step so a half-finished profile isn't treated as ready.
    await saveProfile(currentStep === 'review');

    if (currentStep === 'review') {
      setSubmitted(true);
      return;
    }

    const next = steps[stepIndex + 1];
    if (next) {
      setCurrentStep(next.id);
    }
  };

  const onBack = () => {
    const prev = steps[stepIndex - 1];
    if (prev) {
      setCurrentStep(prev.id);
    }
  };

  const readyForSwipe = useMemo(
    () =>
      Boolean(
        formData.fullName.trim() &&
          formData.provinceCode.trim() &&
          formData.moveInDate &&
          formData.bio.trim() &&
          formData.budgetMin !== null &&
          formData.budgetMax !== null &&
          formData.budgetMax > formData.budgetMin
      ),
    [formData]
  );

  const saveProfile = async (markComplete = false) => {
    if (!currentUser) return;

    try {
      await setDoc(
        doc(firestore, 'users', currentUser.uid),
        {
          role: 'roommate_seeker',
          name: formData.fullName.trim(),
          city: formData.metro || formData.location.trim(), // Use metro or fallback to legacy location
          province: formData.province,
          provinceCode: formData.provinceCode,
          metro: formData.metro,
          occupation: formData.occupation.trim() || 'Not specified',
          budgetMin: formData.budgetMin || 0,
          budgetMax: formData.budgetMax || 0,
          bio: formData.bio.trim(),
          vibe: formData.vibe.trim(),
          dailyRhythms: formData.dailyRhythms,
          lifestyle: formData.lifestyleTags,
          interests: formData.interests,
          amenities: formData.amenities,
          neighborhoods: formData.neighborhoods,
          householdStyles: formData.householdStyles,
          sleepSchedule: formData.sleepSchedule,
          cleanliness: formData.cleanliness,
          petsPreference: formData.hasPets ? 'yes' : 'no',
          smokingPreference: 'outside-only',
          worksRemote: formData.worksRemote,
          moveInDate: formData.moveInDate,
          // Only flip to complete on the final step; omit otherwise so a
          // partially-filled profile (or an existing complete one) is preserved.
          ...(markComplete ? { profileComplete: true } : {}),
          isActive: true,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      // Keep auth context in sync so route guards see the completed profile.
      await refreshProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'basics':
        return (
          <div className="card p-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="onboarding-fullname" className="block text-sm font-semibold text-text-primary mb-2">Full name</label>
                <input
                  id="onboarding-fullname"
                  className="input"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="Who should matches see?"
                />
              </div>
              <div>
                <label htmlFor="onboarding-occupation" className="block text-sm font-semibold text-text-primary mb-2">Occupation</label>
                <input
                  id="onboarding-occupation"
                  className="input"
                  value={formData.occupation}
                  onChange={(e) => handleInputChange('occupation', e.target.value)}
                  placeholder="e.g. Product designer (remote)"
                />
              </div>
              <div className="md:col-span-2">
                <span className="block text-sm font-semibold text-text-primary mb-2">Location</span>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <select
                      aria-label="Province"
                      className="input"
                      value={formData.provinceCode}
                      onChange={(e) => handleProvinceChange(e.target.value)}
                    >
                      <option value="">Select province</option>
                      {provinces.map((province) => (
                        <option key={province.code} value={province.code}>
                          {province.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      aria-label="Metro or city"
                      className="input"
                      value={formData.metro}
                      onChange={(e) => handleMetroChange(e.target.value)}
                      disabled={!formData.provinceCode}
                    >
                      <option value="">Select metro/city</option>
                      {availableMetros.map((metro) => (
                        <option key={metro} value={metro}>
                          {metro}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="hidden md:flex items-center px-4 rounded-xl border-2 border-primary-100 bg-primary-50 text-primary-300 font-semibold text-sm">
                    <MapPin className="w-4 h-4 mr-2" /> {formData.province || 'Select location'}
                  </div>
                </div>
              </div>
              <div>
                <span className="block text-sm font-semibold text-text-primary mb-2">Preferred neighborhoods (optional)</span>
                <div className="flex gap-2 flex-wrap min-h-[44px] p-2 rounded-xl border-2 border-gray-200 bg-gray-50">
                  {availableNeighborhoods.length > 0 ? (
                    availableNeighborhoods.slice(0, 8).map((area) => (
                      <button
                        key={area}
                        type="button"
                        onClick={() => toggleChip('neighborhoods', area)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border-2 transition-all duration-200 ${
                          formData.neighborhoods.includes(area)
                            ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                            : 'bg-white border-gray-200 text-text-secondary hover:border-primary-200'
                        }`}
                      >
                        {area}
                      </button>
                    ))
                  ) : (
                    <span className="text-sm text-text-muted">Select a metro/city to see neighborhoods</span>
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="onboarding-movein" className="block text-sm font-semibold text-text-primary mb-2">Target move-in</label>
                <div className="flex gap-3">
                  <input
                    id="onboarding-movein"
                    type="date"
                    className="input"
                    value={formData.moveInDate}
                    onChange={(e) => handleInputChange('moveInDate', e.target.value)}
                  />
                  <div className="hidden sm:flex items-center px-4 rounded-xl border-2 border-gray-200 text-text-secondary">
                    <Clock className="w-4 h-4 mr-2" />
                    Flexible by &plusmn; 2 weeks
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="onboarding-vibe" className="block text-sm font-semibold text-text-primary mb-2">One-line vibe</label>
                <input
                  id="onboarding-vibe"
                  className="input"
                  value={formData.vibe}
                  onChange={(e) => handleInputChange('vibe', e.target.value)}
                  placeholder="Considerate, balanced, easy-going"
                />
              </div>
            </div>
            <div>
              <label htmlFor="onboarding-bio" className="block text-sm font-semibold text-text-primary mb-2">Short bio</label>
              <textarea
                id="onboarding-bio"
                className="input min-h-[120px]"
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                placeholder="Tell roommates how you live, work, and recharge"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-gradient-to-br from-primary-50 to-white border-2 border-primary-100">
                <div className="flex items-center text-primary-300 font-semibold mb-2">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Tips for a standout profile
                </div>
                <ul className="text-sm text-text-secondary space-y-1">
                  <li>• Mention your schedule and what you value in shared spaces.</li>
                  <li>• Add 2–3 interests to spark conversation.</li>
                  <li>• Keep it warm, honest, and concise.</li>
                </ul>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-accent-blue/10 to-white border-2 border-accent-blue/40">
                <div className="flex items-center text-accent-blue font-semibold mb-2">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Trust & safety
                </div>
                <p className="text-sm text-text-secondary leading-relaxed">
                  RoomZa highlights verified profiles and complete onboarding so you meet roommates who are ready to move.
                </p>
              </div>
            </div>
          </div>
        );
      case 'lifestyle':
        return (
          <div className="card p-6 space-y-6">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="p-3 md:p-4 rounded-xl border-2 border-primary-100 bg-primary-50/60">
                <div className="flex items-center text-primary-300 font-semibold mb-2">
                  <Sun className="w-4 h-4 mr-2" />
                  Sleep schedule
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { key: 'early', label: 'Early riser' },
                    { key: 'flexible', label: 'Flexible' },
                    { key: 'night', label: 'Night owl' },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => handleInputChange('sleepSchedule', option.key as TenantFormData['sleepSchedule'])}
                      className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                        formData.sleepSchedule === option.key
                          ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                          : 'border-gray-200 text-text-secondary hover:border-primary-200'
                      }`}
                    >
                      {option.key === 'night' ? <Moon className="w-3 h-3 md:w-4 md:h-4 inline mr-1 md:mr-2" /> : <Sun className="w-3 h-3 md:w-4 md:h-4 inline mr-1 md:mr-2" />}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-4 rounded-xl border-2 border-primary-100 bg-primary-50/60">
                <div className="flex items-center text-primary-300 font-semibold mb-2">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Cleanliness
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { key: 'tidy', label: 'Tidy & organized' },
                    { key: 'balanced', label: 'Balanced' },
                    { key: 'relaxed', label: 'More relaxed' },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => handleInputChange('cleanliness', option.key as TenantFormData['cleanliness'])}
                      className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                        formData.cleanliness === option.key
                          ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                          : 'border-gray-200 text-text-secondary hover:border-primary-200'
                      }`}
                    >
                      <Sparkles className="w-3 h-3 md:w-4 md:h-4 inline mr-1 md:mr-2" />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-3 md:p-4 rounded-xl border-2 border-primary-100 bg-primary-50/60 sm:col-span-2 md:col-span-1">
                <div className="flex items-center text-primary-300 font-semibold mb-1">
                  <Heart className="w-4 h-4 mr-2" />
                  Daily rhythm
                </div>
                <p className="text-xs text-text-secondary mb-2">Pick all that fit &mdash; e.g. neat and balanced <em>and</em> up for shared dinners</p>
                <div className="flex gap-2 flex-wrap">
                  {vibeOptions.map((vibe) => (
                    <button
                      key={vibe}
                      type="button"
                      onClick={() => toggleChip('dailyRhythms', vibe)}
                      aria-pressed={formData.dailyRhythms.includes(vibe)}
                      className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                        formData.dailyRhythms.includes(vibe)
                          ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                          : 'border-gray-200 text-text-secondary hover:border-primary-200'
                      }`}
                    >
                      {vibe}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-3 md:p-4 rounded-xl border-2 border-gray-200">
                <div className="flex items-center text-text-primary font-semibold mb-3">
                  <Coffee className="w-4 h-4 mr-2 text-primary-300" />
                  Lifestyle tags
                </div>
                <div className="flex gap-2 flex-wrap">
                  {lifestyleOptions.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => toggleChip('lifestyleTags', item)}
                      className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                        formData.lifestyleTags.includes(item)
                          ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                          : 'border-gray-200 text-text-secondary hover:border-primary-200'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-3 md:p-4 rounded-xl border-2 border-gray-200">
                <div className="flex items-center text-text-primary font-semibold mb-3">
                  <Dumbbell className="w-4 h-4 mr-2 text-primary-300" />
                  Interests to match on
                </div>
                <div className="flex gap-2 flex-wrap">
                  {interestOptions.map((interest) => (
                    <button
                      key={interest}
                      type="button"
                      onClick={() => toggleChip('interests', interest)}
                      className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                        formData.interests.includes(interest)
                          ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                          : 'border-gray-200 text-text-secondary hover:border-primary-200'
                      }`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 md:p-4 rounded-xl bg-white border-2 border-gray-200 gap-3">
                <div>
                  <div className="text-text-primary font-semibold">Pets OK?</div>
                  <p className="text-xs md:text-sm text-text-secondary">Tell potential roommates if you can live with pets.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => handleInputChange('hasPets', false)}
                    className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                      formData.hasPets === false
                        ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                        : 'border-gray-200 text-text-secondary hover:border-primary-200'
                    }`}
                  >
                    No pets
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('hasPets', true)}
                    className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                      formData.hasPets
                        ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                        : 'border-gray-200 text-text-secondary hover:border-primary-200'
                    }`}
                  >
                    <PawPrint className="w-3 h-3 md:w-4 md:h-4 inline mr-1 md:mr-2" />
                    Pets welcome
                  </button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 md:p-4 rounded-xl bg-white border-2 border-gray-200 gap-3">
                <div>
                  <div className="text-text-primary font-semibold">Work style</div>
                  <p className="text-xs md:text-sm text-text-secondary">Let roommates plan around calls and deep work.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => handleInputChange('worksRemote', true)}
                    className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                      formData.worksRemote
                        ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                        : 'border-gray-200 text-text-secondary hover:border-primary-200'
                    }`}
                  >
                    <Laptop2 className="w-3 h-3 md:w-4 md:h-4 inline mr-1 md:mr-2" />
                    Remote
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('worksRemote', false)}
                    className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                      !formData.worksRemote
                        ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                        : 'border-gray-200 text-text-secondary hover:border-primary-200'
                    }`}
                  >
                    Hybrid / Office
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case 'home':
        return (
          <div className="card p-6 space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <span className="block text-sm font-semibold text-text-primary mb-2">Your monthly budget range (R)</span>
                <p className="text-xs text-text-secondary mb-2">
                  How much you can contribute toward rent each month — your share, not the full rent. We match you with places you can afford together and split.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <div className="p-3 rounded-xl border-2 border-gray-200">
                    <div className="text-xs text-text-secondary mb-1">Min</div>
                    <input
                      aria-label="Minimum monthly budget in Rand"
                      type="number"
                      className="input"
                      value={formData.budgetMin ?? ''}
                      onChange={(e) => {
                        const nextValue = e.target.value === '' ? null : Number(e.target.value);
                        handleInputChange('budgetMin', nextValue);
                      }}
                    />
                  </div>
                  <div className="p-3 rounded-xl border-2 border-gray-200">
                    <div className="text-xs text-text-secondary mb-1">Max</div>
                    <input
                      aria-label="Maximum monthly budget in Rand"
                      type="number"
                      className="input"
                      value={formData.budgetMax ?? ''}
                      onChange={(e) => {
                        const nextValue = e.target.value === '' ? null : Number(e.target.value);
                        handleInputChange('budgetMax', nextValue);
                      }}
                    />
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-3 bg-gradient-to-r from-primary-200 to-primary-300 rounded-full"
                    style={{ width: `${Math.min(((formData.budgetMax ?? 0) / 12000) * 100, 100)}%` }}
                  ></div>
                </div>
                <p className="text-sm text-text-secondary mt-2 flex items-center">
                  <Wallet className="w-4 h-4 mr-2 text-primary-300" />
                  We use your range to surface homes both of you can afford.
                </p>
              </div>
              <div className="p-4 rounded-2xl bg-gradient-to-br from-primary-200/80 to-accent-purple/80 text-white shadow-lg">
                <div className="flex items-center font-semibold mb-2">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Ready-to-match profile
                </div>
                <ul className="text-sm space-y-1 text-white/90">
                  <li>• Verified identity</li>
                  <li>• Lifestyle signals added</li>
                  <li>• Budget aligned matches first</li>
                </ul>
              </div>
            </div>

            <div>
              <span className="block text-sm font-semibold text-text-primary mb-2">Amenities you want</span>
              <div className="flex gap-2 flex-wrap">
                {amenityOptions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleChip('amenities', item)}
                    className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                      formData.amenities.includes(item)
                        ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                        : 'border-gray-200 text-text-secondary hover:border-primary-200'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-3 md:p-4 rounded-xl border-2 border-gray-200">
                <div className="flex items-center text-text-primary font-semibold mb-3">
                  <MapPin className="w-4 h-4 mr-2 text-primary-300" />
                  Preferred neighborhoods
                </div>
                <p className="text-xs md:text-sm text-text-secondary mb-3">
                  We bias your swipe deck to areas you choose first.
                </p>
                <div className="flex gap-2 flex-wrap">
                  {neighborhoodOptions.map((area) => (
                    <button
                      key={area}
                      type="button"
                      onClick={() => toggleChip('neighborhoods', area)}
                      className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                        formData.neighborhoods.includes(area)
                          ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                          : 'border-gray-200 text-text-secondary hover:border-primary-200'
                      }`}
                    >
                      {area}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-3 md:p-4 rounded-xl border-2 border-gray-200">
                <div className="flex items-center text-text-primary font-semibold mb-3">
                  <Home className="w-4 h-4 mr-2 text-primary-300" />
                  Preferred household style
                </div>
                <div className="flex gap-2 flex-wrap">
                  {householdStyleOptions.map((style) => (
                    <button
                      key={style}
                      type="button"
                      onClick={() => toggleChip('householdStyles', style)}
                      className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                        formData.householdStyles.includes(style)
                          ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                          : 'border-gray-200 text-text-secondary hover:border-primary-200'
                      }`}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="card p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm uppercase tracking-wide text-text-muted font-semibold">Review</p>
                <h2 className="text-2xl font-extrabold text-text-primary mt-1">Looks solid. Ready to match.</h2>
              </div>
              <div className="px-4 py-2 rounded-full bg-primary-200 text-white font-semibold shadow-lg flex items-center">
                <ShieldCheck className="w-4 h-4 mr-2" /> Signal boost active
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl border-2 border-gray-200 bg-white/80">
                <div className="flex items-center text-text-primary font-semibold mb-2">
                  <MapPin className="w-4 h-4 mr-2 text-primary-300" />
                  Where & when
                </div>
                <p className="text-text-primary font-semibold">
                  {formData.metro || formData.province || formData.location || 'Add your location'}
                </p>
                {formData.province && formData.province !== formData.metro && (
                  <p className="text-text-secondary text-sm">{formData.province}</p>
                )}
                {formData.neighborhoods.length > 0 && (
                  <p className="text-text-secondary text-sm">
                    Neighborhoods: {formData.neighborhoods.slice(0, 3).join(', ')}
                    {formData.neighborhoods.length > 3 && ` +${formData.neighborhoods.length - 3} more`}
                  </p>
                )}
                <p className="text-text-secondary text-sm">Move in by {formData.moveInDate}</p>
                <p className="text-text-secondary text-sm mt-2">
                  Budget:{' '}
                  {formData.budgetMin !== null && formData.budgetMax !== null
                    ? `R${formData.budgetMin.toLocaleString()} - R${formData.budgetMax.toLocaleString()}`
                    : 'Add your budget range'}
                </p>
              </div>
              <div className="p-4 rounded-xl border-2 border-gray-200 bg-white/80">
                <div className="flex items-center text-text-primary font-semibold mb-2">
                  <Sparkles className="w-4 h-4 mr-2 text-primary-300" />
                  Lifestyle & vibe
                </div>
                {formData.vibe && <p className="text-text-primary font-semibold">{formData.vibe}</p>}
                {formData.dailyRhythms.length > 0 && (
                  <p className="text-text-secondary text-sm">{formData.dailyRhythms.join(' · ')}</p>
                )}
                <p className="text-text-secondary text-sm">
                  {formData.sleepSchedule === 'early'
                    ? 'Up early and in bed at a decent hour'
                    : formData.sleepSchedule === 'night'
                    ? 'Tends to work later and sleeps in'
                    : formData.sleepSchedule === 'flexible'
                    ? 'Flexible schedule with quiet nights'
                    : 'Add your daily rhythm to match better'}
                </p>
                <p className="text-text-secondary text-sm">
                  Cleanliness:{' '}
                  {formData.cleanliness
                    ? {
                        tidy: 'Tidy & organized',
                        balanced: 'Balanced',
                        relaxed: 'More relaxed',
                      }[formData.cleanliness]
                    : 'Share how you keep your space'}
                </p>
              </div>
              <div className="p-4 rounded-xl border-2 border-gray-200 bg-white/80">
                <div className="flex items-center text-text-primary font-semibold mb-2">
                  <Home className="w-4 h-4 mr-2 text-primary-300" />
                  Must-haves
                </div>
                <div className="flex gap-2 flex-wrap">
                  {formData.amenities.map((item) => (
                    <span
                      key={item}
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-accent-purple/10 text-accent-purple border border-accent-purple/30"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-3 md:p-4 rounded-xl bg-gradient-to-br from-primary-50 to-white border-2 border-primary-100">
              <div className="flex items-center text-primary-300 font-semibold mb-2 text-sm md:text-base">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Matching signals we will highlight
              </div>
              <div className="flex gap-2 md:gap-3 flex-wrap text-xs md:text-sm text-text-secondary">
                <span className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-full bg-white border-2 border-primary-100 font-semibold text-text-primary">Verified identity</span>
                <span className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-full bg-white border-2 border-primary-100 font-semibold text-text-primary">Lifestyle added</span>
                <span className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-full bg-white border-2 border-primary-100 font-semibold text-text-primary">Budget aligned</span>
                <span className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-full bg-white border-2 border-primary-100 font-semibold text-text-primary">Remote-friendly</span>
              </div>
            </div>

            {submitted && (
              <div className="p-3 md:p-4 rounded-xl bg-green-50 border-2 border-semantic-success text-semantic-success text-sm md:text-base font-semibold flex items-center">
                <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" />
                Saved. Your swipe deck will reflect these details.
              </div>
            )}

            <div className="flex flex-wrap gap-2 md:gap-3">
              <button
                type="button"
                onClick={async () => {
                  await saveProfile();
                  navigate('/tenant/swipe');
                }}
                className="btn-primary text-sm md:text-base px-3 py-2 md:px-4 md:py-2"
                disabled={!readyForSwipe}
              >
                Start swiping
              </button>
              <button
                type="button"
                onClick={async () => {
                  await saveProfile();
                  navigate('/tenant/matches');
                }}
                className="btn-secondary text-sm md:text-base px-3 py-2 md:px-4 md:py-2"
              >
                View matches
              </button>
              <button
                type="button"
                onClick={async () => {
                  await saveProfile();
                  navigate('/tenant/profile');
                }}
                className="btn-outline text-sm md:text-base px-3 py-2 md:px-4 md:py-2"
              >
                Preview profile
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-4 py-10 pb-24">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-28 -left-10 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/2 right-6 w-60 h-60 bg-accent-purple rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative max-w-6xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs md:text-sm uppercase tracking-wide text-text-muted font-semibold">Tenant onboarding</p>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-text-primary">Shape your ideal living experience</h1>
            <p className="text-sm md:text-base text-text-secondary mt-2">
              Tell us how you live so we can prioritize the roommates and homes that fit you best.
            </p>
          </div>
          <div className="px-3 py-2 md:px-5 md:py-3 rounded-2xl bg-white border-2 border-primary-100 shadow-lg text-primary-300 text-xs md:text-sm font-semibold flex items-center gap-1.5 md:gap-2">
            <ShieldCheck className="w-4 h-4 md:w-5 md:h-5" /> <span className="hidden sm:inline">Signal boost active</span><span className="sm:hidden">Boosted</span>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-text-secondary">Progress</span>
            <span className="text-sm font-bold text-text-primary">{completion}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              className="h-3 bg-gradient-to-r from-primary-200 via-primary-300 to-accent-purple rounded-full"
              style={{ width: `${completion}%` }}
            ></div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-4 space-y-4">
            <div className="card p-5 space-y-4">
              {steps.map((step, index) => {
                const complete = index < stepIndex || (step.id === currentStep && submitted);
                const active = step.id === currentStep;
                return (
                  <div
                    key={step.id}
                    className={`flex items-start gap-3 p-3 rounded-xl transition-all duration-200 ${
                      active ? 'bg-primary-50 border border-primary-100 shadow-sm' : 'border border-gray-100'
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                        complete || active
                          ? 'bg-gradient-to-br from-primary-200 to-primary-300 text-white border-primary-300'
                          : 'bg-white text-text-secondary border-gray-200'
                      }`}
                    >
                      {complete ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-text-primary font-semibold">
                        {step.icon}
                        {step.title}
                      </div>
                      <p className="text-sm text-text-secondary">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="card p-5">
              <div className="flex items-center text-primary-300 font-semibold mb-2">
                <Heart className="w-4 h-4 mr-2" />
                Matches you want
              </div>
              <p className="text-sm text-text-secondary mb-3">
                Profiles with similar schedules and budget will be shown first. You can adjust later anytime.
              </p>
              <div className="flex gap-2 flex-wrap">
                {formData.budgetMin !== null && formData.budgetMax !== null && (
                  <span className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold bg-accent-blue/10 text-accent-blue border border-accent-blue/30">
                    Budget aligned
                  </span>
                )}
                {formData.worksRemote && (
                  <span className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold bg-accent-purple/10 text-accent-purple border border-accent-purple/30">
                    Remote-friendly
                  </span>
                )}
                {formData.lifestyleTags.includes('Prefers quiet weekday nights') && (
                  <span className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold bg-primary-50 text-primary-300 border border-primary-100">
                    Quiet weekday nights
                  </span>
                )}
                {formData.cleanliness === 'tidy' && (
                  <span className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold bg-primary-50 text-primary-300 border border-primary-100">
                    Tidy & organized
                  </span>
                )}
                {formData.interests.length > 0 && (
                  <span className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold bg-accent-purple/10 text-accent-purple border border-accent-purple/30">
                    {formData.interests.length} shared interest{formData.interests.length > 1 ? 's' : ''}
                  </span>
                )}
                {formData.budgetMin === null && formData.budgetMax === null && !formData.worksRemote && formData.lifestyleTags.length === 0 && formData.interests.length === 0 && (
                  <span className="px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold bg-gray-100 text-text-secondary border border-gray-200">
                    Complete your profile to see match signals
                  </span>
                )}
              </div>
            </div>
          </aside>

          <section className="lg:col-span-8 space-y-4">
            {renderStepContent()}
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              {stepIndex > 0 && (
                <button type="button" onClick={onBack} className="btn-secondary text-sm md:text-base px-3 py-2 md:px-4 md:py-2">
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={onNext}
                disabled={!isStepComplete(currentStep)}
                className="btn-primary text-sm md:text-base px-3 py-2 md:px-4 md:py-2"
              >
                {currentStep === 'review' ? 'Save profile' : 'Next step'}
              </button>
              <span className="text-xs md:text-sm text-text-secondary">
                You can always edit this in your profile.
              </span>
            </div>
          </section>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default TenantOnboarding;

